import { useState, type FormEvent, type ReactNode } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { detailsRefus } from '../../lib/erreurs-api';
import {
  useEnregistrerFinanceur,
  type Financeur,
  type ListeFinanceurs,
  type SaisieFinanceur,
  type TypeFinanceur,
} from '../../lib/useFinancements';

const CLASSE_CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function saisieInitiale(financeur: Financeur | null): SaisieFinanceur {
  return {
    type: financeur?.type ?? 'OPCO',
    raisonSociale: financeur?.raisonSociale ?? '',
    siret: financeur?.siret ?? '',
    adresse: financeur?.adresse ?? '',
    codePostal: financeur?.codePostal ?? '',
    ville: financeur?.ville ?? '',
    emailFacturation: financeur?.emailFacturation ?? '',
    delaiPaiementJours: String(financeur?.delaiPaiementJours ?? 30),
    actif: financeur?.actif ?? true,
  };
}

/**
 * Fiche d'un financeur, client des factures (TF-406) : création ou correction.
 *
 * Nom, adresse et SIRET sont des mentions obligatoires de la facture : la
 * fiche s'enregistre incomplète, c'est l'émission qui les exigera — et l'API
 * qui dit ce qui manque. Le SIRET est contrôlé sur sa clé par l'API.
 */
export function ModaleFinanceur({
  financeur,
  types,
  ouverte,
  onFermer,
}: {
  /** `null` : un financeur à ajouter. */
  financeur: Financeur | null;
  types: ListeFinanceurs['types'];
  ouverte: boolean;
  onFermer: () => void;
}): JSX.Element | null {
  return (
    <Modale
      titre={financeur ? `Fiche de ${financeur.raisonSociale}` : 'Ajouter un financeur'}
      sousTitre="Ce que ses factures diront de lui : raison sociale, adresse, SIRET, délai de règlement."
      ouverte={ouverte}
      onFermer={onFermer}
    >
      {/* Remonté à chaque ouverture : la saisie repart de la fiche. */}
      {ouverte && (
        <Formulaire key={financeur?.id ?? 'nouveau'} financeur={financeur} types={types} onFermer={onFermer} />
      )}
    </Modale>
  );
}

function Formulaire({
  financeur,
  types,
  onFermer,
}: {
  financeur: Financeur | null;
  types: ListeFinanceurs['types'];
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const enregistrer = useEnregistrerFinanceur();
  const [saisie, setSaisie] = useState(() => saisieInitiale(financeur));
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);

  const changer = <K extends keyof SaisieFinanceur>(cle: K, valeur: SaisieFinanceur[K]): void => {
    setSaisie((avant) => ({ ...avant, [cle]: valeur }));
    setErreurs((avant) => {
      const { [cle]: _retire, ...reste } = avant;
      return reste;
    });
    setErreur(null);
  };

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (!saisie.raisonSociale.trim()) {
      setErreurs({ raisonSociale: 'La raison sociale est obligatoire.' });
      return;
    }
    enregistrer.mutate(
      { id: financeur?.id ?? null, saisie },
      {
        onSuccess: ({ financeur: enregistre }) => {
          toast(
            enregistre.etat.pret
              ? `${enregistre.raisonSociale} : fiche prête à facturer.`
              : `${enregistre.raisonSociale} enregistré. ${enregistre.etat.detail ?? ''}`.trim(),
          );
          onFermer();
        },
        onError: (refus) => {
          const details = detailsRefus(refus);
          if (details.length > 0) {
            setErreurs(Object.fromEntries(details.map((detail) => [detail.champ, detail.message])));
            return;
          }
          setErreur(refus instanceof ErreurApi ? refus.message : 'La fiche n’a pas pu être enregistrée.');
        },
      },
    );
  };

  return (
    <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6" aria-label="Fiche du financeur" noValidate>
      <p className="rounded-xl bg-codialis-sand px-3 py-2 text-xs text-codialis-gray">
        Adresse et SIRET sont ceux de la délégation qui facture : ils figurent sur l’accord de prise en charge du
        financeur.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Champ id="financeur-type" libelle="Type" erreur={erreurs['type']}>
          <select
            id="financeur-type"
            value={saisie.type}
            onChange={(e) => changer('type', e.target.value as TypeFinanceur)}
            className={CLASSE_CHAMP}
          >
            {types.map((type) => (
              <option key={type.valeur} value={type.valeur}>
                {type.libelle}
              </option>
            ))}
          </select>
          {saisie.type === 'OPCO' && (
            <p className="mt-1 text-[11px] text-codialis-gray">
              Ses factures porteront la référence de prise en charge (DK) du contrat.
            </p>
          )}
        </Champ>
        <Champ id="financeur-raison" libelle="Raison sociale" erreur={erreurs['raisonSociale']}>
          <input
            id="financeur-raison"
            value={saisie.raisonSociale}
            onChange={(e) => changer('raisonSociale', e.target.value)}
            maxLength={120}
            autoComplete="organization"
            className={CLASSE_CHAMP}
          />
        </Champ>
        <Champ id="financeur-siret" libelle="SIRET" erreur={erreurs['siret']}>
          <input
            id="financeur-siret"
            value={saisie.siret}
            onChange={(e) => changer('siret', e.target.value)}
            inputMode="numeric"
            maxLength={20}
            autoComplete="off"
            spellCheck={false}
            className={`${CLASSE_CHAMP} font-mono`}
          />
        </Champ>
        <Champ id="financeur-email" libelle="E-mail de facturation" erreur={erreurs['emailFacturation']}>
          <input
            id="financeur-email"
            type="email"
            value={saisie.emailFacturation}
            onChange={(e) => changer('emailFacturation', e.target.value)}
            maxLength={200}
            autoComplete="off"
            className={CLASSE_CHAMP}
          />
        </Champ>
        <div className="sm:col-span-2">
          <Champ id="financeur-adresse" libelle="Adresse" erreur={erreurs['adresse']}>
            <input
              id="financeur-adresse"
              value={saisie.adresse}
              onChange={(e) => changer('adresse', e.target.value)}
              maxLength={200}
              autoComplete="street-address"
              placeholder="Numéro et voie, bâtiment"
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <Champ id="financeur-cp" libelle="Code postal" erreur={erreurs['codePostal']}>
          <input
            id="financeur-cp"
            value={saisie.codePostal}
            onChange={(e) => changer('codePostal', e.target.value)}
            inputMode="numeric"
            maxLength={5}
            autoComplete="postal-code"
            className={CLASSE_CHAMP}
          />
        </Champ>
        <Champ id="financeur-ville" libelle="Ville" erreur={erreurs['ville']}>
          <input
            id="financeur-ville"
            value={saisie.ville}
            onChange={(e) => changer('ville', e.target.value)}
            maxLength={80}
            autoComplete="address-level2"
            className={CLASSE_CHAMP}
          />
        </Champ>
        <Champ id="financeur-delai" libelle="Délai de règlement (jours)" erreur={erreurs['delaiPaiementJours']}>
          <input
            id="financeur-delai"
            type="number"
            min={0}
            max={365}
            value={saisie.delaiPaiementJours}
            onChange={(e) => changer('delaiPaiementJours', e.target.value)}
            inputMode="numeric"
            className={CLASSE_CHAMP}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">Il donne la date d’échéance portée sur la facture.</p>
        </Champ>
        <div className="flex min-w-0 items-start sm:pt-6">
          <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm text-codialis-navy">
            <input
              type="checkbox"
              checked={saisie.actif}
              onChange={(e) => changer('actif', e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 accent-codialis-navy"
            />
            <span>
              Financeur actif
              <span className="block text-[11px] text-codialis-gray">
                Désactivé, il ne se choisit plus comme payeur ; il reste aux contrats qui le portent.
              </span>
            </span>
          </label>
        </div>
      </div>

      {erreur && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          {erreur}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
        <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
        <BoutonPrincipal type="submit" disabled={enregistrer.isPending}>
          {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </BoutonPrincipal>
      </div>
    </form>
  );
}

function Champ({
  id,
  libelle,
  erreur,
  children,
}: {
  id: string;
  libelle: string;
  erreur: string | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-xs font-semibold text-codialis-navy">
        {libelle}
      </label>
      {children}
      {erreur && (
        <p role="alert" className="mt-1 text-[11px] font-semibold text-red-700">
          {erreur}
        </p>
      )}
    </div>
  );
}
